/** Colores disponibles para los marcadores del plano. */
export const COLORES_MARCADOR = ['verde', 'naranja', 'rojo', 'azul'] as const;
export type ColorMarcador = (typeof COLORES_MARCADOR)[number];

/** Los colores se guardan en inglés por compatibilidad con lo ya almacenado. */
const A_ALMACENAMIENTO: Readonly<Record<ColorMarcador, string>> = {
  verde: 'green',
  naranja: 'orange',
  rojo: 'red',
  azul: 'blue',
};

const DESDE_ALMACENAMIENTO: Readonly<Record<string, ColorMarcador>> = {
  green: 'verde',
  orange: 'naranja',
  red: 'rojo',
  blue: 'azul',
};

const TOKEN_COLOR: Readonly<Record<ColorMarcador, string>> = {
  verde: '--status-success',
  naranja: '--status-warning',
  rojo: '--status-danger',
  azul: '--status-info',
};

export function colorMarcadorCss(color: ColorMarcador): string {
  return `var(${TOKEN_COLOR[color]})`;
}

export function colorMarcadorAAlmacenamiento(color: ColorMarcador): string {
  return A_ALMACENAMIENTO[color];
}

export function colorMarcadorDesdeAlmacenamiento(valor: unknown): ColorMarcador {
  return DESDE_ALMACENAMIENTO[String(valor ?? '').toLowerCase()] ?? 'verde';
}

/**
 * Marcador colocado sobre el plano de la instalación.
 * `x` e `y` son porcentajes del ancho y alto, de modo que el marcador
 * conserva su sitio aunque la imagen se muestre a otro tamaño.
 */
/**
 * De dónde sale el valor de una tarjeta o un marcador.
 *
 * Al principio sólo había sensores y el identificador bastaba. Un panel útil
 * también quiere saber si un nodo sigue vivo, cuánto consume un medidor o si
 * una bomba está encendida, y todos ellos se numeran por su cuenta: el 7 puede
 * ser un sensor y también un actuador.
 *
 * Lo que se guardó antes no lleva este campo; al leerlo se asume `sensor`, que
 * es lo único que existía.
 */
export const ORIGENES = ['sensor', 'nodo', 'medidor', 'actuador'] as const;

export type OrigenValor = (typeof ORIGENES)[number];

export function origenDesdeAlmacenamiento(valor: unknown): OrigenValor {
  const texto = typeof valor === 'string' ? valor : '';
  return (ORIGENES as readonly string[]).includes(texto) ? (texto as OrigenValor) : 'sensor';
}

/** Cómo se llama cada origen en pantalla. */
export const ETIQUETA_ORIGEN: Readonly<Record<OrigenValor, string>> = {
  sensor: 'Sensor',
  nodo: 'Conexión de un nodo',
  medidor: 'Medidor de energía',
  actuador: 'Actuador',
};

export interface Marcador {
  id: string;
  x: number;
  y: number;
  color: ColorMarcador;
  origen: OrigenValor;
  /**
   * A qué apunta, como texto.
   *
   * De texto y no numérica porque los medidores se identifican con un UUID
   * mientras sensores, nodos y actuadores usan números. Una sola forma para
   * los cuatro evita un campo por tipo.
   *
   * `sensorId` sigue existiendo por lo ya guardado: los paneles anteriores
   * sólo tienen ese campo y hay que poder seguir leyéndolos.
   */
  referencia: string | null;
  sensorId: number | null;
  sensorNombre: string;
  sensorTipo: string;
  sensorArea: string;
  sensorUnidad: string;
}

/** Tarjeta numérica asociada a un sensor. */
export interface TarjetaValor {
  id: string;
  etiqueta: string;
  descripcion: string;
  origen: OrigenValor;
  /** A qué apunta, como texto. Ver `Marcador.referencia`. */
  referencia: string | null;
  sensorId: number | null;
  unidad: string;
  tipo: string;
  area: string;
}

/**
 * Prefijo con el que se guarda una cámara en el mismo campo que la imagen.
 *
 * El panel guarda su fondo en una sola columna de texto. En vez de añadir otra
 * a la base para distinguir imagen de cámara, se marca el valor: una dirección
 * normal es una imagen, y `camara:<id>` es una cámara. Un panel antiguo sigue
 * leyéndose igual porque su valor no lleva prefijo.
 */
const PREFIJO_CAMARA = 'camara:';

/** Qué se ve de fondo en el panel. */
export type FondoPanel =
  { tipo: 'vacio' } | { tipo: 'imagen'; url: string } | { tipo: 'camara'; camaraId: number };

export interface PanelPersonalProps {
  /**
   * Identificador del panel dentro de los de su dueño.
   *
   * Hace falta desde que una persona puede tener varios: el nombre no sirve
   * —se puede repetir y se puede cambiar— y la posición en la lista tampoco,
   * porque cambia al borrar uno de en medio.
   */
  id: string;
  usuarioId: string;
  nombreInstalacion: string;
  imagenUrl: string | null;
  marcadores: readonly Marcador[];
  tarjetas: readonly TarjetaValor[];
}

/** Genera un identificador local para un elemento recién añadido. */
function nuevoId(prefijo: string): string {
  return `${prefijo}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Panel personal de un usuario: un plano de la instalación con marcadores
 * sobre los sensores y tarjetas con sus valores.
 *
 * La entidad es inmutable: cada cambio devuelve un panel nuevo. Así la pantalla
 * puede deshacer, comparar o descartar sin que el estado quede a medias.
 */
export class PanelPersonal {
  constructor(private readonly props: PanelPersonalProps) {}

  static vacio(usuarioId: string, nombre = 'Mi instalación'): PanelPersonal {
    return new PanelPersonal({
      id: nuevoId('p'),
      usuarioId,
      nombreInstalacion: nombre,
      imagenUrl: null,
      marcadores: [],
      tarjetas: [],
    });
  }

  get id(): string {
    return this.props.id;
  }

  get usuarioId(): string {
    return this.props.usuarioId;
  }
  get nombreInstalacion(): string {
    return this.props.nombreInstalacion;
  }
  /**
   * Qué hay de fondo, ya interpretado.
   *
   * La pantalla no debería tener que saber cómo se codifica; pregunta por el
   * fondo y recibe una de las tres formas posibles.
   */
  get fondo(): FondoPanel {
    const valor = this.props.imagenUrl;
    if (!valor) return { tipo: 'vacio' };

    if (valor.startsWith(PREFIJO_CAMARA)) {
      const id = Number(valor.slice(PREFIJO_CAMARA.length));
      return Number.isFinite(id) ? { tipo: 'camara', camaraId: id } : { tipo: 'vacio' };
    }

    return { tipo: 'imagen', url: valor };
  }

  /** Pone una cámara de fondo, sustituyendo lo que hubiera. */
  conCamara(camaraId: number | null): PanelPersonal {
    return new PanelPersonal({
      ...this.props,
      imagenUrl: camaraId === null ? null : `${PREFIJO_CAMARA}${camaraId}`,
    });
  }

  get imagenUrl(): string | null {
    return this.props.imagenUrl;
  }
  get marcadores(): readonly Marcador[] {
    return this.props.marcadores;
  }
  get tarjetas(): readonly TarjetaValor[] {
    return this.props.tarjetas;
  }

  get estaVacio(): boolean {
    return (
      this.props.imagenUrl === null &&
      this.props.marcadores.length === 0 &&
      this.props.tarjetas.length === 0
    );
  }

  /** Sensores a los que este panel hace referencia. */
  get sensoresReferenciados(): number[] {
    const ids = [
      ...this.props.marcadores.map((m) => m.sensorId),
      ...this.props.tarjetas.map((t) => t.sensorId),
    ];
    return [...new Set(ids.filter((id): id is number => id !== null))];
  }

  conNombre(nombreInstalacion: string): PanelPersonal {
    return new PanelPersonal({ ...this.props, nombreInstalacion });
  }

  conImagen(imagenUrl: string | null): PanelPersonal {
    return new PanelPersonal({ ...this.props, imagenUrl });
  }

  /** Añade un marcador en el centro del plano, listo para arrastrarlo. */
  agregarMarcador(color: ColorMarcador = 'verde'): PanelPersonal {
    const marcador: Marcador = {
      id: nuevoId('m'),
      origen: 'sensor',
      referencia: null,
      x: 50,
      y: 50,
      color,
      sensorId: null,
      sensorNombre: 'Nuevo marcador',
      sensorTipo: '',
      sensorArea: '',
      sensorUnidad: '',
    };
    return new PanelPersonal({ ...this.props, marcadores: [...this.props.marcadores, marcador] });
  }

  actualizarMarcador(id: string, cambios: Partial<Marcador>): PanelPersonal {
    return new PanelPersonal({
      ...this.props,
      marcadores: this.props.marcadores.map((m) => (m.id === id ? { ...m, ...cambios } : m)),
    });
  }

  quitarMarcador(id: string): PanelPersonal {
    return new PanelPersonal({
      ...this.props,
      marcadores: this.props.marcadores.filter((m) => m.id !== id),
    });
  }

  agregarTarjeta(): PanelPersonal {
    const tarjeta: TarjetaValor = {
      origen: 'sensor',
      referencia: null,
      id: nuevoId('t'),
      etiqueta: 'Nueva tarjeta',
      descripcion: 'Elige un sensor',
      sensorId: null,
      unidad: '',
      tipo: '',
      area: '',
    };
    return new PanelPersonal({ ...this.props, tarjetas: [...this.props.tarjetas, tarjeta] });
  }

  actualizarTarjeta(id: string, cambios: Partial<TarjetaValor>): PanelPersonal {
    return new PanelPersonal({
      ...this.props,
      tarjetas: this.props.tarjetas.map((t) => (t.id === id ? { ...t, ...cambios } : t)),
    });
  }

  quitarTarjeta(id: string): PanelPersonal {
    return new PanelPersonal({
      ...this.props,
      tarjetas: this.props.tarjetas.filter((t) => t.id !== id),
    });
  }
}
